import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Recently viewed products (BNQ-WEB Sprint 3.2.7).
 *
 * Reuses the native localStorage tracker (`RecentlyViewed`) and the native Section
 * Rendering API pattern already used by predictive search: it reads the stored product
 * ids, excludes the product currently being viewed, then requests this section against
 * the search endpoint (`?q=id:… OR id:…`). The server renders the shared product card
 * grid from `search.results`, which is injected here. The section stays hidden whenever
 * there is no browsing history.
 */
class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    // The section is hidden (display:none) until it has products, which means an
    // IntersectionObserver on this element would never fire. Load on connect instead,
    // deferred to idle time so it never competes with critical rendering. The request
    // is a single, low-priority search-endpoint call driven by localStorage.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.#loadRecentlyViewed(), { timeout: 2000 });
    } else {
      setTimeout(() => this.#loadRecentlyViewed(), 200);
    }
  }

  /**
   * Fetch and inject the recently viewed products, or hide the section when empty.
   */
  async #loadRecentlyViewed() {
    const { sectionId, productId, url: searchUrl } = this.dataset;
    const id = this.id;

    if (!sectionId || !id || !searchUrl) {
      this.#hide();
      return;
    }

    // Exclude the product currently being viewed, then cap at 4 (matches the approved HiFi).
    const viewedProducts = RecentlyViewed.getProducts()
      .filter((/** @type {string} */ pid) => String(pid) !== String(productId))
      .slice(0, 4);

    if (viewedProducts.length === 0) {
      this.#hide();
      return;
    }

    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', viewedProducts.map((/** @type {string} */ pid) => `id:${pid}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.#hide();
        return;
      }

      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const fresh = parsed.querySelector(`recently-viewed[id="${id}"]`);

      if (fresh && fresh.querySelector("[data-has-recently-viewed='true']")) {
        this.innerHTML = fresh.innerHTML;
      } else {
        this.#hide();
      }
    } catch (error) {
      this.#hide();
    }
  }

  #hide() {
    this.classList.add('hidden');
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewedProducts);
}
